// ============================================
// ADMIN HEADER WITH NOTIFICATIONS
// ============================================

import { useState, useEffect, useRef } from 'react';
import { Bell, Search, User, Settings, LogOut, Loader2, Menu } from 'lucide-react';
import { cn, getInitials } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '@crew-at-sea/api-client';
import { LanguageSwitcher, useTranslation } from '@crew-at-sea/i18n';

interface AdminHeaderProps {
  className?: string;
  onMenuToggle?: () => void;
}

export function AdminHeader({ className, onMenuToggle }: AdminHeaderProps) {
  const { t } = useTranslation('common');
  const { user, logout } = useAuthStore();
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Fetch notifications
  const { data: notificationsResponse, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.getNotifications(),
    refetchInterval: 60000,
  });

  // Fetch unread count
  const { data: unreadResponse } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsApi.getUnreadCount(),
    refetchInterval: 30000,
  });

  const notifications: any[] = (notificationsResponse as any)?.data || notificationsResponse || [];
  const unreadCount: number = (unreadResponse as any)?.data?.count || (unreadResponse as any)?.count || 0;

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatTimeAgo = (date: string) => {
    const now = new Date();
    const past = new Date(date);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('timeAgo.justNow');
    if (diffMins < 60) return t('timeAgo.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('timeAgo.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('timeAgo.daysAgo', { count: diffDays });
    return past.toLocaleDateString(t('language') === 'Dil' ? 'tr-TR' : 'en-US');
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'APPLICATION': return '📝';
      case 'JOB': return '💼';
      case 'VERIFICATION': return '✅';
      case 'REVIEW': return '⭐';
      case 'SYSTEM': return '🔔';
      default: return '📢';
    }
  };

  return (
    <header
      className={cn(
        'flex items-center justify-between h-16 px-4 md:px-6 bg-white border-b border-gray-200',
        className
      )}
    >
      {/* Sol: Hamburger + Search */}
      <div className="flex items-center gap-3 flex-1">
        {/* Mobil hamburger menü */}
        {onMenuToggle && (
          <button
            onClick={onMenuToggle}
            className="lg:hidden p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-maritime transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>
        )}

        {/* Search — mobilde gizli */}
        <div className="hidden md:block flex-1 max-w-xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              className="w-full h-10 pl-10 pr-4 text-sm bg-gray-50 border border-gray-200 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500 focus:border-maritime-500"
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-4">
        {/* Dil Değiştirme */}
        <LanguageSwitcher />

        {/* Notifications */}
        <div className="relative" ref={notificationRef}>
          <button 
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-maritime transition-colors"
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] text-xs font-medium text-white bg-red-500 rounded-full px-1">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notifications Dropdown */}
          {showNotifications && (
            <div className="absolute right-0 top-full mt-2 w-80 md:w-96 bg-white border border-gray-200 rounded-maritime shadow-maritime-lg z-50">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">{t('notifications')}</h3>
                {unreadCount > 0 && (
                  <button
                    onClick={() => markAllAsReadMutation.mutate()}
                    className="text-xs text-maritime-600 hover:text-maritime-700 font-medium"
                    disabled={markAllAsReadMutation.isPending}
                  >
                    {markAllAsReadMutation.isPending ? t('processing') : t('markAllRead')}
                  </button>
                )}
              </div>

              <div className="max-h-96 overflow-y-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-maritime-600" />
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="py-8 text-center text-gray-500">
                    <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>{t('noNotifications')}</p>
                  </div>
                ) : (
                  notifications.slice(0, 10).map((notification: any) => (
                    <div
                      key={notification.id}
                      className={cn(
                        'px-4 py-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors',
                        !notification.isRead && 'bg-maritime-50/50'
                      )}
                      onClick={() => {
                        if (!notification.isRead) {
                          markAsReadMutation.mutate(notification.id);
                        }
                      }}
                    >
                      <div className="flex gap-3">
                        <span className="text-xl">{getNotificationIcon(notification.type)}</span>
                        <div className="flex-1 min-w-0">
                          <p className={cn(
                            'text-sm',
                            !notification.isRead ? 'font-medium text-gray-900' : 'text-gray-600'
                          )}>
                            {notification.title}
                          </p>
                          {notification.message && (
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                              {notification.message}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {formatTimeAgo(notification.createdAt)}
                          </p>
                        </div>
                        {!notification.isRead && (
                          <span className="w-2 h-2 bg-maritime-500 rounded-full flex-shrink-0 mt-2" />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {notifications.length > 0 && (
                <div className="px-4 py-3 border-t border-gray-200">
                  <a
                    href="/notifications"
                    className="text-sm text-maritime-600 hover:text-maritime-700 font-medium"
                  >
                    {t('allNotifications')}
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="relative group">
          <button className="flex items-center gap-3 p-2 text-gray-700 hover:bg-gray-100 rounded-maritime transition-colors">
            <div className="flex items-center justify-center w-8 h-8 bg-maritime-100 text-maritime-700 font-medium rounded-full">
              {user ? getInitials(user.email?.split('@')[0] || '') : 'AD'}
            </div>
            <div className="hidden md:block text-left">
              <p className="text-sm font-medium">{user?.email || t('admin')}</p>
              <p className="text-xs text-gray-500">{t('admin')}</p>
            </div>
          </button>

          {/* Dropdown */}
          <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded-maritime shadow-maritime-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
            <div className="py-1">
              <a
                href="/settings/profile"
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <User className="h-4 w-4" />
                {t('profile')}
              </a>
              <a
                href="/settings"
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Settings className="h-4 w-4" />
                {t('settings')}
              </a>
              <hr className="my-1 border-gray-200" />
              <button
                onClick={() => {
                  logout();
                  window.location.href = '/login';
                }}
                className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
              >
                <LogOut className="h-4 w-4" />
                {t('logout')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
